import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Store as StoreIcon, Zap, Coins, Wallet, Search, ShieldCheck, Package, Star, X, ArrowDownAZ, TrendingUp, CircleDollarSign, Heart, Flame } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import AppRow from "@/components/AppRow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ActivationWizard from "@/components/ActivationWizard";
import PixCheckout from "@/components/PixCheckout";

const SORTS = [
  { id: "az", label: "A-Z", icon: ArrowDownAZ },
  { id: "hot", label: "Em alta", icon: TrendingUp },
  { id: "price", label: "Preço", icon: CircleDollarSign },
  { id: "fav", label: "Favoritos", icon: Heart },
];
const priceStr = (p) => (p != null ? Number(p).toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : null);

export default function Store() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const FAV_KEY = `vulpix_store_favs_${slug}`;
  const loadFavs = () => { try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || "[]")); } catch { return new Set(); } };

  const [store, setStore] = useState(undefined);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("az");
  const [favs, setFavs] = useState(loadFavs);
  const [selected, setSelected] = useState(null);
  const [payMethod, setPayMethod] = useState("pix");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [pixOpen, setPixOpen] = useState(false);
  const [mobilePayOpen, setMobilePayOpen] = useState(false);

  useEffect(() => {
    api.get(`/reseller/store-public/${slug}`).then(({ data }) => {
      setStore(data);
      if (!data.pix_enabled) setPayMethod("credits");
      if (data.apps?.length) setSelected([...data.apps].sort((a, b) => (b.activations_count || 0) - (a.activations_count || 0))[0]);
    }).catch(() => setStore(null));
  }, [slug]);

  const accent = store?.store_color || "#e11d2a";
  const hotIds = useMemo(() => {
    if (!store) return new Set();
    return new Set([...store.apps].filter((a) => (a.activations_count || 0) > 0)
      .sort((a, b) => (b.activations_count || 0) - (a.activations_count || 0)).slice(0, 5).map((a) => a.id));
  }, [store]);

  const toggleFav = (id) => setFavs((prev) => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id);
    localStorage.setItem(FAV_KEY, JSON.stringify([...next])); return next;
  });

  const list = useMemo(() => {
    if (!store) return null;
    const term = q.trim().toLowerCase();
    let arr = store.apps.filter((a) => !term || a.name?.toLowerCase().includes(term) || a.category?.toLowerCase().includes(term));
    if (sort === "fav") arr = arr.filter((a) => favs.has(a.id));
    const byName = (a, b) => String(a.name).localeCompare(String(b.name), "pt-BR");
    if (sort === "price") arr = [...arr].sort((a, b) => (a.price ?? 1e9) - (b.price ?? 1e9) || byName(a, b));
    else if (sort === "hot") arr = [...arr].sort((a, b) => (b.activations_count || 0) - (a.activations_count || 0) || byName(a, b));
    else arr = [...arr].sort(byName);
    return arr;
  }, [store, q, sort, favs]);

  if (store === undefined) return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Carregando...</div>;
  if (store === null) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center grid-bg">
      <StoreIcon className="h-10 w-10 text-primary" />
      <h1 className="mt-4 font-heading text-2xl font-bold">Loja não encontrada</h1>
      <p className="text-sm text-muted-foreground">Verifique o link e tente novamente.</p>
    </div>
  );

  const doActivate = () => {
    if (payMethod === "pix") return setPixOpen(true);
    if (!user) return navigate(`/register?r=${store.reseller_id}&loja=${slug}`);
    setWizardOpen(true);
  };

  const PayPanel = ({ mobile }) => (
    <>
      <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Forma de pagamento</div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button onClick={() => { if (store.pix_enabled) setPayMethod("pix"); }} disabled={!store.pix_enabled} data-testid={`pay-pix${mobile ? "-m" : ""}`}
          className={`rounded-lg border p-3 text-left transition-colors ${!store.pix_enabled ? "opacity-50 cursor-not-allowed border-border" : payMethod === "pix" ? "ring-2" : "border-border hover:bg-muted/60"}`}
          style={store.pix_enabled && payMethod === "pix" ? { borderColor: accent, backgroundColor: `${accent}14`, boxShadow: `0 0 0 2px ${accent}44` } : {}}>
          <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> Pix</div>
          <div className="mt-1 font-num text-lg font-bold">{store.pix_enabled ? (priceStr(selected?.price) ? `R$ ${priceStr(selected.price)}` : "—") : "Indisponível"}</div>
        </button>
        <button onClick={() => (user ? setPayMethod("credits") : navigate("/login"))} data-testid={`pay-credits${mobile ? "-m" : ""}`}
          className={`relative rounded-lg border p-3 text-left transition-colors ${payMethod === "credits" ? "ring-2" : "border-border hover:bg-muted/60"}`}
          style={payMethod === "credits" ? { borderColor: accent, backgroundColor: `${accent}14`, boxShadow: `0 0 0 2px ${accent}44` } : {}}>
          {!user && <span className="absolute right-2 top-2 rounded-sm bg-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase text-muted-foreground">Entrar</span>}
          <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"><Coins className="h-3.5 w-3.5" /> Créditos</div>
          <div className="mt-1 font-num text-lg font-bold">{selected?.credit_cost}</div>
        </button>
      </div>
      <Button className="mt-4 h-12 w-full gap-2 rounded-lg text-base text-white hover:opacity-90" style={{ backgroundColor: accent }} data-testid={`store-activate${mobile ? "-m" : ""}`}
        onClick={() => { if (mobile) setMobilePayOpen(false); doActivate(); }}>
        <Zap className="h-5 w-5" /> Ativar agora
      </Button>
      <p className="mt-2 text-center text-[11px] text-muted-foreground">
        {payMethod === "pix" ? "Pague via Pix e ativamos automaticamente após a confirmação." : "Créditos só são debitados se a ativação for concluída."}
      </p>
    </>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="public-store">
      <header className="sticky top-0 z-30 border-b glass bg-background/70" style={{ borderColor: accent }}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border" style={{ borderColor: `${accent}55` }}>
              {store.store_logo ? <img src={`${API}${store.store_logo}`} alt="" className="h-full w-full object-contain" /> : <StoreIcon className="h-5 w-5" style={{ color: accent }} />}
            </span>
            <span className="font-heading text-lg font-bold">{store.store_name}</span>
            {(store.activations_today > 0 || store.activations_7d > 0) && (
              <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2.5 py-1 text-xs font-semibold text-green-500" data-testid="activations-today-badge">
                <Flame className="h-3.5 w-3.5" /> {store.activations_today > 0 ? `${store.activations_today} hoje` : `${store.activations_7d} em 7 dias`}
              </span>
            )}
          </div>
          <ThemeToggle />
        </div>
      </header>

      <div className="sticky top-[57px] z-20 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 h-6 w-6 -translate-y-1/2" style={{ color: accent }} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar e ativar aplicativo..." data-testid="store-search-input"
              className="h-16 rounded-xl border-2 bg-card pl-14 pr-12 text-lg font-medium sm:text-xl focus-visible:ring-0" style={{ borderColor: `${accent}66` }} />
            {q && <button onClick={() => setQ("")} data-testid="search-clear" className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>}
          </div>
        </div>
      </div>

      {selected && (
        <div className="sticky top-[138px] z-10 border-b border-border bg-card/95 backdrop-blur lg:hidden" data-testid="mobile-selected-bar">
          <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5 sm:px-6">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
              {selected.logo ? <img src={selected.logo} alt="" className="h-full w-full object-cover" onError={(e) => (e.target.style.display = "none")} /> : <Package className="h-5 w-5 text-muted-foreground" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate font-heading text-sm font-bold">{selected.name}</div>
              <div className="font-num text-xs text-muted-foreground">R$ {priceStr(selected.price)} · {selected.credit_cost} cr.</div>
            </div>
            <Button size="sm" className="shrink-0 gap-1.5 text-white" style={{ backgroundColor: accent }} data-testid="mobile-activate-btn" onClick={() => setMobilePayOpen(true)}><Zap className="h-4 w-4" /> Ativar</Button>
          </div>
        </div>
      )}

      <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="mb-3 flex items-center gap-1 overflow-x-auto no-scrollbar border-b border-border">
              {SORTS.map((s) => (
                <button key={s.id} onClick={() => setSort(s.id)} data-testid={`sort-${s.id}`}
                  className="-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors"
                  style={sort === s.id ? { borderColor: accent, color: "hsl(var(--foreground))" } : { borderColor: "transparent", color: "hsl(var(--muted-foreground))" }}>
                  <s.icon className="h-4 w-4" /> {s.label}
                </button>
              ))}
              <span className="ml-auto hidden shrink-0 pr-1 text-xs text-muted-foreground sm:block">{list ? `${list.length} apps` : ""}</span>
            </div>
            <div className="scroll-thin max-h-[640px] overflow-y-auto rounded-xl border border-border bg-card">
              {list === null && <div className="space-y-1 p-3">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-md" />)}</div>}
              {list && list.length === 0 && (
                <div className="flex flex-col items-center gap-2 py-20 text-center text-muted-foreground" data-testid="apps-empty">
                  <Package className="h-8 w-8" />{sort === "fav" ? "Nenhum favorito ainda. Toque na estrela." : "Nenhum aplicativo encontrado."}
                </div>
              )}
              {list && list.map((app) => (
                <AppRow key={app.id} app={app} selected={selected?.id === app.id} hot={hotIds.has(app.id)} fav={favs.has(app.id)} onSelect={setSelected} onToggleFav={toggleFav} />
              ))}
            </div>
          </div>

          <div className="hidden lg:col-span-4 lg:block">
            <div className="lg:sticky lg:top-44 space-y-4">
              <div className="rounded-xl border p-4 text-center" style={{ borderColor: `${accent}44`, backgroundColor: `${accent}0d` }}>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest" style={{ color: accent }}><ShieldCheck className="h-3.5 w-3.5" /> Ativação anual · na hora</span>
              </div>
              <div className="overflow-hidden rounded-xl border border-border bg-card" data-testid="action-panel">
                <div className="border-b border-border bg-muted/40 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest" style={{ color: accent }}>Ação e pagamento</div>
                {!selected ? (
                  <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-muted-foreground"><Package className="h-7 w-7" /> Selecione um aplicativo.</div>
                ) : (
                  <div className="p-5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
                        {selected.logo ? <img src={selected.logo} alt="" className="h-full w-full object-cover" onError={(e) => (e.target.style.display = "none")} /> : <Package className="h-6 w-6 text-muted-foreground" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-heading text-lg font-bold" data-testid="panel-app-name">{selected.name}</div>
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{selected.category || "Player"}</div>
                      </div>
                      <button onClick={() => toggleFav(selected.id)} data-testid="panel-fav" style={{ color: favs.has(selected.id) ? accent : undefined }} className={favs.has(selected.id) ? "" : "text-muted-foreground/50"}>
                        <Star className="h-5 w-5" style={{ fill: favs.has(selected.id) ? accent : "transparent" }} />
                      </button>
                    </div>
                    <div className="mt-4"><PayPanel mobile={false} /></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={mobilePayOpen} onOpenChange={setMobilePayOpen}>
        <DialogContent data-testid="mobile-pay-dialog">
          <DialogHeader><DialogTitle>{selected?.name}</DialogTitle></DialogHeader>
          {selected && <PayPanel mobile={true} />}
        </DialogContent>
      </Dialog>

      <footer className="border-t border-border py-6 text-center">
        <a href="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground" data-testid="powered-by-vulpix">
          <Zap className="h-3.5 w-3.5 text-primary" /> Powered by <span className="font-heading font-bold text-foreground">VULPIX</span>
        </a>
      </footer>

      <ActivationWizard app={selected} open={wizardOpen} onOpenChange={setWizardOpen} />
      <PixCheckout app={selected} open={pixOpen} onOpenChange={setPixOpen} resellerId={store.reseller_id} pixProvider={store.pix_provider} />
    </div>
  );
}
